import { useCallback, useEffect, useRef, useState } from 'react'
import { request } from '@papan/api-client'
import { useShell, useKeybookAvailable, keybookAvailable as keybookAvailableNow, type ShellAppKind } from '@papan/core-ui/ui'
import { isFolderColorKey, type MiniappIconSpec } from '@papan/types'

/**
 * Server persistence for the shell app dock (Phase 1b of
 * docs/plans/2026-07-05_OS_SHELL_REDESIGN.md).
 *
 * - Hydrates the running set from /api/shell/instances on boot and on
 *   identity switch, so background apps survive a reload.
 * - Write-through: when the URL enters (or moves within) a deck app,
 *   debounce-PATCHes the instance's stored route, so a reload restores each
 *   hidden window to its last-owned page.
 *
 * Workspaces are excluded — their running-state stays on
 * /api/workspaces/open, and the shell unions the two lists.
 */

interface ShellInstanceRow {
  kind: string
  ref: string
  route: string
  openOrder: number
}

interface ShellPinRow {
  kind: string
  ref: string
  title?: string | null
  pinOrder: number
  /** 0195 — absent from an older API mid-deploy; absent means on the rail. */
  folderId?: string | null
  /** The app's CURRENT icon, resolved server-side from the app folder rather
   *  than snapshotted on the pin. Absent from an older API; absent means the
   *  tile falls back to the name-derived default, which is what every pinned
   *  tile did before this rode along. */
  icon?: MiniappIconSpec | null
  iconRev?: string | null
}

interface ShellFolderRow {
  id: string
  title: string
  pinOrder: number
  /** 0196 — absent from an older API; absent means the default. */
  color?: string | null
}

// MUST stay in sync with `SYSTEM_KINDS` in features/workspace/api/shell-routes.ts
// — the server validates `kind` against its own copy and 400s on anything it
// doesn't know. Both write paths here swallow errors (a failed persist is meant
// to be non-fatal), so a kind that is in this set and NOT in the server's fails
// silently: the app looks persisted and simply isn't after a reload. `billing`
// was in exactly that state until 2026-08-06.
export const SYSTEM_PERSISTED_KINDS = new Set<ShellAppKind>(['drive', 'workspace', 'chat', 'keybook', 'settings', 'billing', 'launcher', 'apps'])
const USER_KINDS = new Set<ShellAppKind>(['miniapp', 'html'])
const ROUTE_WRITE_DEBOUNCE_MS = 800

export function useShellPersistence(opts: {
  tenantId: string
  isReady: boolean
  activeOrgEmail?: string
  deckKind: ShellAppKind | null
  pathname: string
  search: string
  /** Bumped when another tab or device changed this identity's dock
   *  (`shell:changed`): re-read pins and folders — never the running set. */
  dockRevision?: number
}) {
  const { tenantId, isReady, activeOrgEmail, deckKind, pathname, search, dockRevision = 0 } = opts
  const { openApp, closeApp, isRunning, hydrateDock } = useShell()

  // 0179: the keybook is a persisted system kind, and on a keybook-disabled
  // deployment a persisted/registered instance is a phantom — the taskbar
  // item that carries the only close affordance is hidden. Three defences,
  // because the availability store seeds `true` and the /api/public/experience
  // fetch races both hydration and the first write-through:
  //   1. hydrate skips keybook instances while unavailable;
  //   2. the write-through refuses to persist a keybook route while
  //      unavailable;
  //   3. the sweep below runs whenever availability RESOLVES false and
  //      un-registers + un-persists whatever slipped through either race (or
  //      predates the flip). Fire-and-forget like every other persist call.
  const keybookAvailable = useKeybookAvailable()
  const shellRef = useRef({ closeApp, isRunning })
  shellRef.current = { closeApp, isRunning }
  useEffect(() => {
    if (keybookAvailable || !isReady) return
    if (shellRef.current.isRunning('keybook')) shellRef.current.closeApp('keybook')
    void closeShellInstance('keybook').catch(() => {})
  }, [keybookAvailable, isReady])

  // Restored routes keyed by the tenant they belong to — the consumer only
  // seeds frozen locations when restored.tenantId matches, which kills the
  // stale-seed race across a tenant switch. `userRoutes` is keyed by ref
  // (user apps can run several instances; each restores to its own page).
  const [restored, setRestored] = useState<{ tenantId: string; routes: Partial<Record<ShellAppKind, string>>; userRoutes: Record<string, string> } | null>(null)
  const hydratedForRef = useRef<string | null>(null)

  /** Read pins + folders and replace the dock. `isCancelled` lets a stale
   *  fetch (identity switched meanwhile) drop its result. On failure the FIRST
   *  read starts empty (nothing is known yet); a RE-read keeps what the tab
   *  holds — wiping a full rail because one refresh failed is worse than a
   *  stale one. */
  const loadDock = useCallback((isCancelled: () => boolean = () => false, onError: 'clear' | 'keep' = 'clear') => {
    return request<{ pins: ShellPinRow[]; folders?: ShellFolderRow[] }>('/api/shell/pins')
      .then(({ pins, folders }) => {
        if (isCancelled()) return
        hydrateDock(
          pins
            .filter(p => USER_KINDS.has(p.kind as ShellAppKind) && p.ref)
            .map(p => ({
              kind: p.kind as ShellAppKind,
              ref: p.ref,
              title: p.title ?? undefined,
              pinOrder: p.pinOrder,
              folderId: p.folderId ?? null,
              icon: p.icon ?? null,
              iconRev: p.iconRev ?? null,
            })),
          // `folders` is absent from an older API during a rolling restart;
          // every pin then reads as on the rail, which is the safe reading.
          (folders ?? []).map(f => ({ id: f.id, title: f.title ?? '', pinOrder: f.pinOrder, color: isFolderColorKey(f.color) ? f.color : null })),
        )
      })
      .catch(() => { if (!isCancelled() && onError === 'clear') hydrateDock([], []) })
  }, [hydrateDock])

  // Another tab or device changed the dock: re-read it. The running set is
  // NOT re-read — it would re-open windows this tab closed on purpose.
  useEffect(() => {
    if (dockRevision === 0 || !isReady || !tenantId) return
    // A REAL cancel guard, not `() => false`. The identity is in the deps, so
    // switching tenant or org email tears this effect down — and without the
    // guard an already-in-flight read would resolve afterwards and hydrate the
    // PREVIOUS identity's pins into the new one's rail. It is the exact hazard
    // `isCancelled` exists for, and it only became reachable when
    // `shell:changed` started being delivered at all.
    let cancelled = false
    void loadDock(() => cancelled, 'keep')
    return () => { cancelled = true }
  }, [dockRevision, isReady, tenantId, activeOrgEmail, loadDock])

  // ── Hydrate on boot / identity switch ────────────────────────────────
  useEffect(() => {
    if (!isReady || !tenantId) return
    const key = `${tenantId}:${activeOrgEmail ?? ''}`
    if (hydratedForRef.current === key) return
    hydratedForRef.current = key
    let cancelled = false
    // Pins hydrate in parallel — they render on the dock even when the app
    // isn't running (relaunchable), so failure to load just means an
    // unpinned-looking dock, recoverable on the next identity change.
    loadDock(() => cancelled)
    request<{ instances: ShellInstanceRow[] }>('/api/shell/instances')
      .then(({ instances }) => {
        if (cancelled) return
        const routes: Partial<Record<ShellAppKind, string>> = {}
        const userRoutes: Record<string, string> = {}
        for (const inst of instances) {
          const kind = inst.kind as ShellAppKind
          // Defence 1 — see the 0179 note above. Reads the store directly
          // rather than the hook value so a mid-flight flip is honoured.
          if (kind === 'keybook' && !keybookAvailableNow()) continue
          if (SYSTEM_PERSISTED_KINDS.has(kind)) {
            openApp(kind)
            routes[kind] = inst.route
          } else if (USER_KINDS.has(kind) && inst.ref) {
            // User apps: title resolves lazily inside AppWindow. The route
            // may carry an in-app splat (/~/app/<ref>/<page>) — the shell
            // seeds it as the window's frozen location for deep restore.
            openApp(kind, inst.ref)
            userRoutes[inst.ref] = inst.route
          }
        }
        setRestored({ tenantId, routes, userRoutes })
      })
      .catch(() => {
        // Non-fatal: the dock just starts with only the URL's app running.
        // Allow a retry on the next identity change.
        if (hydratedForRef.current === key) hydratedForRef.current = null
      })
    return () => { cancelled = true }
  }, [tenantId, isReady, activeOrgEmail, openApp, hydrateDock, loadDock])

  // ── Write-through: persist the active app + its current route ────────
  const writeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (!isReady || !deckKind || !SYSTEM_PERSISTED_KINDS.has(deckKind)) return
    // Defence 2 — see the 0179 note above.
    if (deckKind === 'keybook' && !keybookAvailable) return
    // Wait for hydration so the first write can't race the read.
    if (hydratedForRef.current !== `${tenantId}:${activeOrgEmail ?? ''}`) return
    if (writeTimerRef.current) clearTimeout(writeTimerRef.current)
    const route = pathname + search
    writeTimerRef.current = setTimeout(() => {
      request('/api/shell/instances/open', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-Shell-Client': SHELL_CLIENT_ID },
        body: JSON.stringify({ kind: deckKind, route }),
      }).catch(() => {})
    }, ROUTE_WRITE_DEBOUNCE_MS)
    return () => {
      if (writeTimerRef.current) clearTimeout(writeTimerRef.current)
    }
  }, [deckKind, pathname, search, tenantId, isReady, activeOrgEmail, keybookAvailable])

  return restored
}

/** Persist an opened instance (user apps register here on first open —
 *  system apps flow through the write-through effect instead). */
/** This tab's id, sent with every dock write and echoed on the resulting
 *  `shell:changed`, so the tab can tell its own announcements from others'. */
export const SHELL_CLIENT_ID = `shell-${Math.random().toString(36).slice(2, 12)}`

export async function persistShellOpen(kind: ShellAppKind, ref: string, route: string): Promise<void> {
  await request('/api/shell/instances/open', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'X-Shell-Client': SHELL_CLIENT_ID },
    body: JSON.stringify({ kind, ref, route }),
  })
}

/** Close an app server-side (dock close button on user apps). */
export async function closeShellInstance(kind: ShellAppKind, ref = ''): Promise<void> {
  await request('/api/shell/instances/close', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'X-Shell-Client': SHELL_CLIENT_ID },
    body: JSON.stringify({ kind, ref }),
  })
}

/** Pin a user app server-side (idempotent; refreshes the stored title). */
export async function pinShellApp(kind: ShellAppKind, ref: string, title?: string): Promise<void> {
  await request('/api/shell/pins/add', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'X-Shell-Client': SHELL_CLIENT_ID },
    body: JSON.stringify({ kind, ref, title }),
  })
}

/** Unpin a user app server-side. */
export async function unpinShellApp(kind: ShellAppKind, ref: string): Promise<void> {
  await request('/api/shell/pins/remove', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'X-Shell-Client': SHELL_CLIENT_ID },
    body: JSON.stringify({ kind, ref }),
  })
}

// ── Folders (0195) ─────────────────────────────────────────────────────
// Same fire-and-forget contract as the pin writes above for move / rename /
// delete. Create is the one call whose RESULT the shell needs (the server
// mints the id), so its caller awaits it.

const JSON_HEADERS = { 'Content-Type': 'application/json', 'X-Shell-Client': SHELL_CLIENT_ID }

/** Create a folder from two or more PINNED apps. Resolves to the server id. */
export async function createShellFolder(
  members: Array<{ kind: ShellAppKind; ref: string }>,
  title = '',
): Promise<{ id: string }> {
  return request<{ id: string }>('/api/shell/folders', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ title, members }),
  })
}

/** Move a pinned app into a folder, or back to the rail with `null`. */
export async function setShellPinFolder(kind: ShellAppKind, ref: string, folderId: string | null): Promise<void> {
  await request('/api/shell/pins/folder', {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify({ kind, ref, folderId }),
  })
}

/** Rename and/or recolour; only the keys present are sent. */
export async function updateShellFolder(id: string, patch: { title?: string; color?: string | null }): Promise<void> {
  await request(`/api/shell/folders/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(patch),
  })
}

/** Ungroup — the members return to the rail server-side (ON DELETE SET NULL). */
export async function deleteShellFolder(id: string): Promise<void> {
  // `JSON_HEADERS` for the `X-Shell-Client` half, NOT the content type (a DELETE
  // sends no body). Without it the server announces `clientId: undefined`, the
  // acting tab fails its own self-echo check and re-reads the dock — which is
  // the revert this header exists to prevent, and here it would land on top of
  // the 6s Undo and wipe the folder the user just restored.
  await request(`/api/shell/folders/${encodeURIComponent(id)}`, { method: 'DELETE', headers: JSON_HEADERS })
}

/** The rail's top-level order (`app:<kind>:<ref>` / `folder:<id>`), whole-list. */
export async function setShellRailOrder(order: string[]): Promise<void> {
  await request('/api/shell/rail', {
    method: 'PUT',
    headers: JSON_HEADERS,
    body: JSON.stringify({ order }),
  })
}

/** The member order inside one folder (`app:<kind>:<ref>` keys), whole-list. */
export async function setShellFolderOrder(folderId: string, order: string[]): Promise<void> {
  await request(`/api/shell/folders/${encodeURIComponent(folderId)}/order`, {
    method: 'PUT',
    headers: JSON_HEADERS,
    body: JSON.stringify({ order }),
  })
}
